import { motion } from 'framer-motion';
import { Clock, Users, ThumbsUp } from 'lucide-react';

const values = [
  { icon: Clock, title: 'Experienced', desc: 'With over a decade of experience, our team of skilled electricians is equipped to handle any electrical challenge with precision and expertise.' },
  { icon: Users, title: 'Reliable', desc: 'We show up on time, communicate clearly, and take the time to explain your options so you can make informed decisions about your property.' },
  { icon: ThumbsUp, title: 'Detail-Oriented', desc: "We don\u2019t just get the job done\u2014we do it right the first time, ensuring long-term safety and performance on every project we undertake." },
];

const FADE_OFFSET_LG = 30;

const fadeUp = {
  hidden: { opacity: 0, y: FADE_OFFSET_LG },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } }),
};
const viewportOnce = { once: true };

function AboutStats() {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp} custom={3} className="flex gap-8">
      {[{ val: '600+', label: 'Projects' }, { val: '10+', label: 'Years' }, { val: 'Yes', label: 'Emergency' }].map((stat, i) => (
        <div key={stat.label} className="flex items-center gap-0">
          {i > 0 && <div className="w-px bg-zinc-200 mr-8" style={{ height: '4rem' }} />}
          <div>
            <p className="font-display text-5xl sm:text-6xl text-black">{stat.val}</p>
            <p className="font-body text-xs uppercase tracking-wider text-zinc-400 mt-1">{stat.label}</p>
          </div>
        </div>
      ))}
    </motion.div>
  );
}

function ValueCard({ value, index }) {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp} custom={index + 2}
      className="border border-black p-8 hover:bg-yellow-400 hover:text-black transition-all duration-200 group"
      data-testid={`about-value-${value.title.toLowerCase()}`}>
      <value.icon className="w-8 h-8 text-yellow-500 mb-4 group-hover:text-black transition-colors" />
      <h3 className="font-heading text-lg font-bold uppercase tracking-tight mb-3">{value.title}</h3>
      <p className="font-body text-sm text-zinc-600 leading-relaxed group-hover:text-black/80 transition-colors">{value.desc}</p>
    </motion.div>
  );
}

export default function AboutSection() {
  return (
    <section id="about" data-testid="about-section" className="py-24 sm:py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <motion.p initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp} custom={0}
              className="font-body text-xs uppercase tracking-[0.2em] text-zinc-400 mb-3">What Sets Us Apart</motion.p>
            <motion.h2 initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp} custom={1}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase mb-6">Clients Choose Us</motion.h2>
            <motion.p initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp} custom={2}
              className="font-body text-zinc-600 leading-relaxed mb-8">
              Our attention to detail, deep technical knowledge, and unwavering commitment to customer satisfaction. Whether you need a service panel upgrade, EV charger installation, backup power system, or complete wiring for a new build, we bring precision and professionalism to every project.
            </motion.p>
            <AboutStats />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {values.map((v, i) => <ValueCard key={v.title} value={v} index={i} />)}
          </div>
        </div>
      </div>
    </section>
  );
}
